import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Shield, 
  LayoutDashboard, 
  Activity, 
  Server, 
  Lock, 
  AlertTriangle, 
  Search, 
  Bug, 
  FileCheck, 
  Zap, 
  Settings, 
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  LogOut,
  User,
  Crosshair
} from 'lucide-react';
import { cn } from '@/lib/utils';
import useAuthStore from '@/store/authStore';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const menuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
  { icon: Activity, label: 'Security Score', path: '/security-score' },
  { icon: Server, label: 'Assets', path: '/assets' },
  { icon: Lock, label: 'NAC', path: '/nac' },
  { icon: AlertTriangle, label: 'Alerts & Incidents', path: '/alerts' },
  { icon: Crosshair, label: 'War Room', path: '/war-room' },
  { icon: Search, label: 'Threat Detection', path: '/threats' },
  { icon: Shield, label: 'Threat Intel', path: '/threat-intel' },
  { icon: Bug, label: 'Vulnerabilities', path: '/vulnerabilities' },
  { icon: FileCheck, label: 'Compliance', path: '/compliance' },
  { icon: Zap, label: 'Automation', path: '/automation' },
  { icon: Settings, label: 'Settings', path: '/settings' },
  { icon: HelpCircle, label: 'Help', path: '/help' },
];

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside 
      className={cn(
        "fixed left-0 top-0 h-full border-r border-border bg-card/50 backdrop-blur-xl z-50 transition-all duration-300",
        collapsed ? "w-16" : "w-64"
      )}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="flex items-center h-16 px-4 border-b border-border">
        <Link to="/" className="flex items-center gap-3" data-testid="sidebar-logo">
          <Shield className="h-8 w-8 text-success" />
          {!collapsed && (
            <span className="font-display font-bold text-xl uppercase tracking-wider text-foreground">
              InfraShield
            </span>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <ul className="space-y-1 px-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-sm transition-all duration-200",
                    "hover:bg-primary/20 hover:text-primary-foreground",
                    isActive 
                      ? "bg-primary text-primary-foreground shadow-[0_0_15px_rgba(30,58,95,0.5)]" 
                      : "text-muted-foreground"
                  )}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  {!collapsed && (
                    <span className="font-display text-sm uppercase tracking-wider">
                      {item.label}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User section */}
      <div className="border-t border-border p-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              className={cn(
                "w-full justify-start gap-3 px-3 py-2",
                collapsed && "justify-center px-0"
              )}
              data-testid="user-menu-trigger"
            >
              <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                <User className="h-4 w-4 text-primary-foreground" />
              </div>
              {!collapsed && (
                <div className="flex flex-col items-start">
                  <span className="text-sm font-medium">{user?.name || user?.full_name || user?.email}</span>
                  <span className="text-xs text-muted-foreground">{user?.role}</span>
                </div>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              <Settings className="mr-2 h-4 w-4" />
              Configurações
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="mr-2 h-4 w-4" />
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Collapse button */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/80 transition-colors"
        data-testid="sidebar-toggle"
      >
        {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
      </button>
    </aside>
  );
}

export default Sidebar;
